import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, ApiRequestError } from "../api/client";
import { useCurrentCase } from "../hooks/useCurrentCase";
import { PRICING } from "../../shared/constants";
import { BankBox } from "../components/BankBox";

interface PageItem {
  key: string;
  label: string;
  required: boolean;
  image_url: string | null;
}
interface SitemapRes {
  status: string;
  job: { id: string; status: string; error: string | null } | null;
  pages: PageItem[];
}
interface Invoice {
  id: string;
  amount_total: number;
  status: string;
  due_at: string | null;
}

const yen = (n: number) => `${n.toLocaleString("ja-JP")}円`;

// No.9 下層ページ生成・サイト構成確認 → 承認 → 初期費用請求。
export function SitemapPage() {
  const { caseRow, loading } = useCurrentCase();
  const navigate = useNavigate();
  const [data, setData] = useState<SitemapRes | null>(null);
  const [included, setIncluded] = useState<Record<string, boolean>>({});
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!caseRow) return;
    const caseId = caseRow.id;
    let stopped = false;
    const poll = async () => {
      try {
        const res = await api.get<SitemapRes>(`/api/cases/${caseId}/sitemap`);
        if (stopped) return;
        setData(res);
        setIncluded((prev) =>
          Object.keys(prev).length
            ? prev
            : Object.fromEntries(res.pages.map((p) => [p.key, true])),
        );
        if (res.job?.status === "done" || res.job?.status === "failed") {
          if (timer.current) clearInterval(timer.current);
        }
      } catch {
        /* retry next tick */
      }
    };
    void poll();
    timer.current = setInterval(poll, 3000);
    return () => {
      stopped = true;
      if (timer.current) clearInterval(timer.current);
    };
  }, [caseRow]);

  const approve = async () => {
    if (!caseRow) return;
    setError(null);
    setApproving(true);
    try {
      const res = await api.post<{ invoice: Invoice }>(`/api/cases/${caseRow.id}/approve`, {});
      setInvoice(res.invoice);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "承認に失敗しました");
    } finally {
      setApproving(false);
    }
  };

  if (loading) return <div className="page-center">読み込み中...</div>;
  if (!caseRow) return <div className="page-center">案件が見つかりません。</div>;

  const jobDone = data?.job?.status === "done";
  const jobFailed = data?.job?.status === "failed";

  if (invoice) {
    return (
      <section className="wizard">
        <div className="wizard-head">
          <span className="step-tag">承認完了</span>
          <h1>初期費用のご請求</h1>
          <p className="muted">
            デザインを承認いただきありがとうございます。下記口座へお振込みください。入金確認後にコーディングへ着手します。
          </p>
        </div>
        <div className="card pay-panel">
          <div className="pay-amount">
            <span className="muted">初期費用（税込総額）</span>
            <strong>{yen(invoice.amount_total)}</strong>
          </div>
          <BankBox />
          <p className="muted small">
            ※振込手数料はお客様のご負担となります。入金確認は運営にて手動で行い、確認後ステータスが「制作中」に変わります。通知はマイページに表示されます（メール送信はありません）。
          </p>
          <Link to="/dashboard" className="btn btn-primary btn-lg">
            ダッシュボードへ →
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="wizard">
      <div className="wizard-head">
        <span className="step-tag">No.9</span>
        <h1>下層ページ生成・サイト構成確認</h1>
        <p className="muted">
          AIが提案したサイト構成と各ページのプレビューです。内容をご確認のうえ、承認してください。
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {jobFailed ? (
        <div className="card">
          <div className="alert alert-error">下層ページの生成に失敗しました。もう一度お試しください。</div>
          <button className="btn btn-primary" onClick={() => navigate("/design/tune")}>
            デザイン確定に戻る
          </button>
        </div>
      ) : !jobDone ? (
        <div className="card generating">
          <div className="progress-head">
            <div className="progress-percent">生成中</div>
            <div className="muted small">下層ページの構成とデザインを準備しています…</div>
          </div>
          <div className="progress-bar">
            <div className="progress-fill indeterminate" />
          </div>
        </div>
      ) : (
        <>
          <div className="wizard-grid">
            <div className="card">
              <h2>サイト構成</h2>
              <div className="sitemap-grid">
                {data?.pages.map((p) => (
                  <div key={p.key} className={`sitemap-page ${included[p.key] ? "on" : "off"}`}>
                    <div className="sitemap-page-head">
                      <span className="sitemap-label">{p.label}</span>
                      <span className={`tag ${p.required ? "tag-req" : "tag-opt"}`}>
                        {p.required ? "必須" : "任意"}
                      </span>
                    </div>
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.label} />
                    ) : (
                      <div className="proposal-noimg muted">準備中</div>
                    )}
                    <label className="include-toggle">
                      <input
                        type="checkbox"
                        checked={included[p.key] ?? true}
                        disabled={p.required}
                        onChange={(e) =>
                          setIncluded((prev) => ({ ...prev, [p.key]: e.target.checked }))
                        }
                      />
                      このページを含める
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <aside className="wizard-aside">
              <div className="card">
                <h2>構成サマリー</h2>
                <dl className="summary-dl">
                  <dt>ページ数</dt>
                  <dd>{data?.pages.length ?? 0}ページ</dd>
                  <dt>必須ページ</dt>
                  <dd>{data?.pages.filter((p) => p.required).length ?? 0}ページ</dd>
                  <dt>任意ページ</dt>
                  <dd>{data?.pages.filter((p) => !p.required).length ?? 0}ページ</dd>
                </dl>
                <p className="muted small">
                  承認すると初期費用 {yen(PRICING.initialFee)}（税込総額）をご請求します。
                  登録時の特典として{PRICING.initialCreditGrant.toLocaleString("ja-JP")}クレジットも付与されます。
                  お支払いは銀行振込のみです。
                </p>
                <button className="btn btn-primary btn-block btn-lg" onClick={approve} disabled={approving}>
                  {approving ? "承認処理中..." : "承認して初期費用のお支払いへ →"}
                </button>
              </div>
            </aside>
          </div>

          <div className="wizard-actions">
            <button className="btn btn-ghost" onClick={() => navigate("/design/tune")}>
              ← デザイン微調整に戻る
            </button>
          </div>
        </>
      )}
    </section>
  );
}
